import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';

interface KollelTransferConfirmDialogProps {
  open: boolean;
  avrechName: string;
  fromKollelName: string;
  toKollelName: string;
  onApprove: () => void;
  onReject: () => void;
}

/**
 * "בקשה להעברת כולל" (HAT-1446 phase 7, Figma node 10444:126286's Supporters_Popups variant): shown
 * the moment a coordinator picks an avrech who is currently registered in a DIFFERENT kollel of the
 * same fund. There is no separate approval queue or second party here - a fund has one coordinator
 * view over all its kollels, so this is that same coordinator confirming a consequential action on
 * the spot, not asking someone else. The warning matters because it is a point of no return: once
 * approved, the avrech's row in the old kollel is gone (KollelsService.UpdateKollelAvrechsAsync),
 * so no further support requests can be filed for them under it.
 */
const KollelTransferConfirmDialog = ({
  open,
  avrechName,
  fromKollelName,
  toKollelName,
  onApprove,
  onReject,
}: KollelTransferConfirmDialogProps) => {
  const { t } = useTranslation();

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) onReject();
      }}
      title={t('kollels.wizard.transferConfirm.dialogTitle')}
    >
      <div className="flex flex-col gap-5 p-6 items-center text-center max-w-[550px]">
        <h2 className="heading-h2-bold text-(--font-body-1)">
          {t('kollels.wizard.transferConfirm.title', { name: avrechName })}
        </h2>

        <p className="body-lr-regular text-(--font-body-1)">
          {t('kollels.wizard.transferConfirm.body', { name: avrechName, fromKollel: fromKollelName, toKollel: toKollelName })}
        </p>

        <div className="bg-(--surface-grey-low-2) rounded-lg p-3 text-right w-full">
          <p className="body-sm-regular text-(--font-body-2)">
            {t('kollels.wizard.transferConfirm.warning', { fromKollel: fromKollelName })}
          </p>
        </div>

        <div className="flex flex-col gap-3 w-full mt-2">
          <Button variant="primary" onClick={onApprove}>
            {t('kollels.wizard.transferConfirm.approve')}
          </Button>
          <Button variant="outline" onClick={onReject}>
            {t('kollels.wizard.transferConfirm.reject')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default KollelTransferConfirmDialog;
